import { useMemo } from 'react';

import { KubernetesRegistryFormValues } from '@/react/portainer/policies/components/kubernetes/registry/types';
import { useRegistries } from '@/react/portainer/registries/queries/useRegistries';
import { useIsPureAdmin } from '@/react/hooks/useUser';

import { FormSection } from '@@/form-components/FormSection';
import { Link } from '@@/Link';
import { InlineLoader } from '@@/InlineLoader';
import { TextTip } from '@@/Tip/TextTip';

import { PolicyEmptyState } from './PolicyEmptyState';
import { PolicyTableWrapper } from './PolicyTableWrapper';

interface Props {
  formData: { accessRows: KubernetesRegistryFormValues['accessRows'] };
}

/**
 * Read-only table display of Registry policy access list with clean visual styling.
 */
export function RegistryPolicyReadOnly({ formData }: Props) {
  const { accessRows } = formData;
  const isPureAdmin = useIsPureAdmin();
  const registriesQuery = useRegistries({
    // ID 0 represents the built-in Docker Hub (anonymous) registry; omit it here.
    select: (registries) => registries.filter((r) => r.Id !== 0),
  });

  const registryMap = useMemo(
    () => new Map((registriesQuery.data || []).map((r) => [r.Id, r.Name])),
    [registriesQuery.data]
  );

  if (accessRows.length === 0) {
    return (
      <PolicyEmptyState
        title="Registry Access"
        message="No registry access configured for this policy."
      />
    );
  }

  if (registriesQuery.isLoading) {
    return (
      <FormSection title="Registry Access">
        <InlineLoader>Loading registries...</InlineLoader>
      </FormSection>
    );
  }

  if (registriesQuery.isError) {
    return (
      <FormSection title="Registry Access">
        <TextTip color="red">
          Failed to load registries. Please try again.
        </TextTip>
      </FormSection>
    );
  }

  return (
    <FormSection title="Registry Access">
      <PolicyTableWrapper
        caption="Registry access configuration for this policy"
        data-cy="registry-policy-readonly-table"
      >
        <thead className="bg-gray-3 th-dark:bg-gray-9">
          <tr>
            <th className="px-4 py-3 text-left text-sm font-medium">
              Registry
            </th>
            <th className="px-4 py-3 text-left text-sm font-medium">
              Namespaces
            </th>
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-5 th-dark:divide-gray-7">
          {accessRows.map((row) => (
            <tr
              key={row.registryId}
              className="bg-gray-2 th-dark:bg-gray-iron-10"
            >
              <td className="px-4 py-3">
                {isPureAdmin ? (
                  <Link
                    to="portainer.registries.registry"
                    params={{ id: row.registryId }}
                    className="text-sm font-medium"
                    data-cy={`registry-link-${row.registryId}`}
                  >
                    {registryMap.get(row.registryId) || 'Unknown'}
                  </Link>
                ) : (
                  <span
                    className="text-sm font-medium"
                    data-cy={`registry-link-${row.registryId}`}
                  >
                    {registryMap.get(row.registryId) || 'Unknown'}
                  </span>
                )}
              </td>
              <td className="px-4 py-3 text-sm">
                {row.namespaces?.length > 0 ? (
                  <span className="font-medium">
                    {row.namespaces.join(', ')}
                  </span>
                ) : (
                  <span className="text-muted italic">All namespaces</span>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </PolicyTableWrapper>
    </FormSection>
  );
}
